import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["image", "canvas", "placeholder", "zoneList"]
  static values = {
    zones: Array,
    imageUrl: String,
    placedText: String,
    unplacedText: String
  }

  connect() {
    this.markers = new Map()
    this.draggingMarker = null
    this.dragOffset = { x: 0, y: 0 }
    this.imgBounds = null

    if (this.imageUrlValue) {
      this.loadImage(this.imageUrlValue)
    }

    this.initializeMarkers()
    this.bindEvents()

    // Reposition on resize
    this.boundResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.boundResize)

    // Use IntersectionObserver to detect when canvas becomes visible
    // (e.g. when the Media tab is shown - panels start hidden so bounds are 0)
    if (this.hasCanvasTarget) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.hasImageTarget && this.imageTarget.complete && this.imageTarget.naturalWidth > 0) {
            this.imgBounds = null
            this.calculateImageBounds()
            this.repositionMarkers()
          }
        })
      })
      this.visibilityObserver.observe(this.canvasTarget)
    }
  }

  disconnect() {
    this.unbindEvents()
    window.removeEventListener('resize', this.boundResize)
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
  }

  bindEvents() {
    this.boundMouseMove = this.handleMouseMove.bind(this)
    this.boundMouseUp = this.handleMouseUp.bind(this)
    document.addEventListener('mousemove', this.boundMouseMove)
    document.addEventListener('mouseup', this.boundMouseUp)
  }

  unbindEvents() {
    document.removeEventListener('mousemove', this.boundMouseMove)
    document.removeEventListener('mouseup', this.boundMouseUp)
  }

  handleResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.calculateImageBounds()
      this.repositionMarkers()
    }, 100)
  }

  // Handle file selection
  fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      this.loadImage(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  loadImage(url) {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add('hidden')
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.classList.remove('hidden')
      this.canvasTarget.classList.add('has-image')
    }
    if (this.hasImageTarget) {
      this.imageTarget.onload = () => {
        this.calculateImageBounds()
        this.repositionMarkers()
      }
      this.imageTarget.src = url
      this.imageTarget.classList.remove('hidden')
    }
  }

  calculateImageBounds() {
    if (!this.hasImageTarget || !this.hasCanvasTarget) return

    const img = this.imageTarget
    const canvas = this.canvasTarget
    const canvasRect = canvas.getBoundingClientRect()

    if (!img.naturalWidth || !img.naturalHeight) return

    const imgNaturalRatio = img.naturalWidth / img.naturalHeight
    const canvasRatio = canvasRect.width / canvasRect.height

    let imgWidth, imgHeight, imgLeft, imgTop

    if (imgNaturalRatio > canvasRatio) {
      imgWidth = canvasRect.width
      imgHeight = canvasRect.width / imgNaturalRatio
      imgLeft = 0
      imgTop = (canvasRect.height - imgHeight) / 2
    } else {
      imgHeight = canvasRect.height
      imgWidth = canvasRect.height * imgNaturalRatio
      imgLeft = (canvasRect.width - imgWidth) / 2
      imgTop = 0
    }

    this.imgBounds = { width: imgWidth, height: imgHeight, left: imgLeft, top: imgTop }
  }

  initializeMarkers() {
    if (!this.hasCanvasTarget) return

    this.zonesValue.forEach(zone => {
      this.createMarker(zone)
    })
  }

  createMarker(zone) {
    const marker = document.createElement('div')
    marker.className = 'map-marker'
    marker.dataset.zoneId = zone.id
    marker.dataset.mapX = zone.map_x
    marker.dataset.mapY = zone.map_y
    marker.style.backgroundColor = zone.color || '#00d4aa'
    marker.innerHTML = `
      <span class="map-marker-label">${zone.code || zone.name.substring(0, 3)}</span>
    `

    const isPlaced = zone.map_x !== null && zone.map_y !== null

    if (isPlaced) {
      marker.classList.add('placed')
      marker.addEventListener('mousedown', (e) => this.startDrag(e, marker, zone))
      this.canvasTarget.appendChild(marker)
    } else {
      marker.classList.add('unplaced')
      // Don't add to canvas - unplaced markers stay hidden until placed via list click
    }

    this.markers.set(zone.id, { element: marker, zone })
  }

  // Place an unpositioned zone at the center of the map (triggered from zone list click)
  placeZone(event) {
    event.preventDefault()
    event.stopPropagation()

    const listItem = event.currentTarget
    const zoneId = parseInt(listItem.dataset.zoneListId)
    const markerData = this.markers.get(zoneId)

    if (!markerData) return

    this.calculateImageBounds()
    if (!this.imgBounds) return

    const { element: marker, zone } = markerData

    // Place at center (50%, 50%)
    const centerX = 50
    const centerY = 50

    marker.dataset.mapX = centerX
    marker.dataset.mapY = centerY
    marker.classList.remove('unplaced')
    marker.classList.add('placed')

    // Add to canvas if not already there
    if (!marker.parentElement || marker.parentElement !== this.canvasTarget) {
      marker.addEventListener('mousedown', (e) => this.startDrag(e, marker, zone))
      this.canvasTarget.appendChild(marker)
    }

    // Position visually
    const pixelX = this.imgBounds.left + (centerX / 100) * this.imgBounds.width
    const pixelY = this.imgBounds.top + (centerY / 100) * this.imgBounds.height
    marker.style.left = `${pixelX}px`
    marker.style.top = `${pixelY}px`

    // Update hidden inputs and list UI
    this.updateZonePosition(zoneId, centerX, centerY)

    // Remove click-to-place from the list item
    listItem.classList.remove('zone-list-item--unplaced')
    listItem.removeAttribute('data-action')
    listItem.removeAttribute('title')
    listItem.style.cursor = ''
  }

  startDrag(event, marker, zone) {
    event.preventDefault()
    this.calculateImageBounds()
    this.draggingMarker = { marker, zone }

    const rect = marker.getBoundingClientRect()
    this.dragOffset = {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2
    }

    marker.classList.add('dragging')
  }

  handleMouseMove(event) {
    if (!this.draggingMarker || !this.imgBounds) return

    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const { left: imgLeft, top: imgTop, width: imgWidth, height: imgHeight } = this.imgBounds

    let x = event.clientX - canvasRect.left - this.dragOffset.x
    let y = event.clientY - canvasRect.top - this.dragOffset.y

    // Constrain to image bounds (not canvas)
    x = Math.max(imgLeft, Math.min(x, imgLeft + imgWidth))
    y = Math.max(imgTop, Math.min(y, imgTop + imgHeight))

    this.draggingMarker.marker.style.left = `${x}px`
    this.draggingMarker.marker.style.top = `${y}px`
  }

  handleMouseUp(event) {
    if (!this.draggingMarker || !this.imgBounds) return

    const { marker, zone } = this.draggingMarker
    marker.classList.remove('dragging')
    marker.classList.add('placed')
    marker.classList.remove('unplaced')

    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const { left: imgLeft, top: imgTop, width: imgWidth, height: imgHeight } = this.imgBounds

    let x = event.clientX - canvasRect.left
    let y = event.clientY - canvasRect.top

    // Constrain to image bounds
    x = Math.max(imgLeft, Math.min(x, imgLeft + imgWidth))
    y = Math.max(imgTop, Math.min(y, imgTop + imgHeight))

    // Convert to percentage relative to image
    const percentX = ((x - imgLeft) / imgWidth) * 100
    const percentY = ((y - imgTop) / imgHeight) * 100

    // Store for repositioning
    marker.dataset.mapX = percentX
    marker.dataset.mapY = percentY

    // Update hidden inputs
    this.updateZonePosition(zone.id, percentX, percentY)

    this.draggingMarker = null
  }

  updateZonePosition(zoneId, x, y) {
    // Update hidden inputs
    const xInput = document.querySelector(`input[name="zones[${zoneId}][map_x]"]`)
    const yInput = document.querySelector(`input[name="zones[${zoneId}][map_y]"]`)

    if (xInput) xInput.value = typeof x === 'number' ? x.toFixed(2) : ''
    if (yInput) yInput.value = typeof y === 'number' ? y.toFixed(2) : ''

    this.updateZoneListItem(zoneId, x, y)
  }

  updateZoneListItem(zoneId, x, y) {
    const listItem = document.querySelector(`[data-zone-list-id="${zoneId}"]`)
    if (!listItem) return

    const isPlaced = x !== null && x !== '' && y !== null && y !== ''
    const placedText = this.placedTextValue || 'Ubicada'
    const unplacedText = this.unplacedTextValue || 'Sin ubicar'

    const statusBadge = listItem.querySelector('.zone-status')
    if (statusBadge) {
      if (isPlaced) {
        statusBadge.className = 'zone-status badge badge-success'
        statusBadge.textContent = placedText
      } else {
        statusBadge.className = 'zone-status badge badge-neutral'
        statusBadge.textContent = unplacedText
      }
    }

    // Show/hide remove button
    const removeBtn = listItem.querySelector('.zone-remove')
    if (removeBtn) {
      if (isPlaced) {
        removeBtn.classList.remove('hidden')
      } else {
        removeBtn.classList.add('hidden')
      }
    }
  }

  repositionMarkers() {
    if (!this.imgBounds) {
      this.calculateImageBounds()
    }
    if (!this.imgBounds) return

    const { left: imgLeft, top: imgTop, width: imgWidth, height: imgHeight } = this.imgBounds

    this.markers.forEach(({ element }) => {
      const mapX = parseFloat(element.dataset.mapX)
      const mapY = parseFloat(element.dataset.mapY)

      if (!isNaN(mapX) && !isNaN(mapY) && element.classList.contains('placed')) {
        const pixelX = imgLeft + (mapX / 100) * imgWidth
        const pixelY = imgTop + (mapY / 100) * imgHeight
        element.style.left = `${pixelX}px`
        element.style.top = `${pixelY}px`
      }
    })
  }

  // Remove marker position
  removeMarker(event) {
    event.stopPropagation()
    const zoneId = event.currentTarget.dataset.zoneId
    const markerData = this.markers.get(parseInt(zoneId))

    if (markerData) {
      markerData.element.classList.remove('placed')
      markerData.element.classList.add('unplaced')
      markerData.element.style.left = ''
      markerData.element.style.top = ''
      // Remove from canvas
      if (markerData.element.parentElement === this.canvasTarget) {
        this.canvasTarget.removeChild(markerData.element)
      }
    }

    this.updateZonePosition(zoneId, '', '')
    this.updateZoneListItem(zoneId, null, null)

    // Restore click-to-place on the list item
    const listItem = document.querySelector(`[data-zone-list-id="${zoneId}"]`)
    if (listItem) {
      listItem.classList.add('zone-list-item--unplaced')
      listItem.dataset.action = 'click->map-editor#placeZone'
      listItem.style.cursor = 'pointer'
    }
  }
}
